import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  dedupeCaseTrackingLookupByName,
  listCaseTrackingAgencies,
  listCaseTrackingTaskStatuses,
  listCaseTrackingUsers,
  mapCaseTrackingLookupNamesToIds,
  type CaseTrackingLookupOption,
} from '../../services/caseTrackingReports';
import { listWorkflowPerformanceWorkflowStagesCascade } from '../../services/workflowPerformanceReports';
import { getFileCategories, getSubFileCategories, getUrgencyLevels } from '../../services/criminalCaseRequests';
import { REPORT_SUBMITTED_AGENCIES, REPORT_TASK_STATUSES } from '../../data/reports';

function namesFromOptions(options: CaseTrackingLookupOption[], fallback: readonly string[]): string[] {
  if (options.length) return options.map((o) => o.name);
  return [...fallback];
}

export function useCaseTrackingLookups(
  selectedFileCategoryNames: string[],
  selectedSubFileCategoryNames: string[],
) {
  const { user } = useAuth();
  const [agencyOptions, setAgencyOptions] = useState<CaseTrackingLookupOption[]>([]);
  const [taskStatusOptions, setTaskStatusOptions] = useState<CaseTrackingLookupOption[]>([]);
  const [workflowStageOptionsAll, setWorkflowStageOptionsAll] = useState<CaseTrackingLookupOption[]>([]);
  const [userOptionsAll, setUserOptionsAll] = useState<CaseTrackingLookupOption[]>([]);
  const [fileCategoryOptions, setFileCategoryOptions] = useState<CaseTrackingLookupOption[]>([]);
  const [subFileCategoryOptions, setSubFileCategoryOptions] = useState<CaseTrackingLookupOption[]>([]);
  const [urgencyLevelOptions, setUrgencyLevelOptions] = useState<CaseTrackingLookupOption[]>([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [resolvedSubCategoryKey, setResolvedSubCategoryKey] = useState<string | null>(null);
  const [resolvedWorkflowStageKey, setResolvedWorkflowStageKey] = useState<string | null>(null);

  const subCategoryRequestKey = useMemo(() => {
    if (!selectedFileCategoryNames.length) return '';
    const categoryIds = selectedFileCategoryNames
      .map((name) => fileCategoryOptions.find((c) => c.name === name)?.id)
      .filter((id): id is string => Boolean(id));
    return categoryIds.length ? [...categoryIds].sort().join('|') : '';
  }, [selectedFileCategoryNames, fileCategoryOptions]);

  const workflowStageRequestKey = useMemo(() => {
    if (!selectedFileCategoryNames.length) return '';

    const categoryIds = selectedFileCategoryNames
      .map((name) => fileCategoryOptions.find((c) => c.name === name)?.id)
      .filter((id): id is string => Boolean(id));
    if (categoryIds.length === 0) return '|pending|';

    const subCategoryIds = selectedSubFileCategoryNames
      .map((name) => subFileCategoryOptions.find((s) => s.name === name)?.id)
      .filter((id): id is string => Boolean(id));
    if (selectedSubFileCategoryNames.length > 0 && subCategoryIds.length === 0) return '|pending|';

    return JSON.stringify({
      categoryIds: [...categoryIds].sort(),
      subCategoryIds: [...subCategoryIds].sort(),
    });
  }, [
    selectedFileCategoryNames,
    selectedSubFileCategoryNames,
    fileCategoryOptions,
    subFileCategoryOptions,
  ]);

  const subLookupsLoading =
    subCategoryRequestKey !== '' && resolvedSubCategoryKey !== subCategoryRequestKey;
  const workflowStagesLoading =
    lookupsLoaded &&
    (workflowStageRequestKey === '|pending|' ||
      (workflowStageRequestKey !== '' && resolvedWorkflowStageKey !== workflowStageRequestKey));
  const activeSubFileCategoryOptions =
    subCategoryRequestKey === '' || resolvedSubCategoryKey !== subCategoryRequestKey
      ? []
      : subFileCategoryOptions;
  const activeWorkflowStageOptions = useMemo(
    () =>
      workflowStageRequestKey === '' || resolvedWorkflowStageKey !== workflowStageRequestKey
        ? []
        : workflowStageOptionsAll,
    [workflowStageRequestKey, resolvedWorkflowStageKey, workflowStageOptionsAll],
  );

  const workflowStageOptions = useMemo(
    () => dedupeCaseTrackingLookupByName(activeWorkflowStageOptions),
    [activeWorkflowStageOptions],
  );

  const userOptions = useMemo(() => dedupeCaseTrackingLookupByName(userOptionsAll), [userOptionsAll]);

  const mapUserNamesToIds = (names: string[]) =>
    mapCaseTrackingLookupNamesToIds(userOptionsAll, names, true);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;

    void Promise.all([
      listCaseTrackingAgencies(),
      listCaseTrackingTaskStatuses(),
      listCaseTrackingUsers(),
      getFileCategories(),
      getUrgencyLevels(),
    ]).then(([agencies, statuses, users, cats, urgency]) => {
      if (!alive) return;
      if (agencies.success) setAgencyOptions(agencies.data);
      if (statuses.success) setTaskStatusOptions(statuses.data);
      if (users.success) setUserOptionsAll(users.data);
      if (cats.success) setFileCategoryOptions(cats.data.map((c) => ({ id: c.id, name: c.name })));
      if (urgency.success) setUrgencyLevelOptions(urgency.data.map((u) => ({ id: u.id, name: u.name })));
      setLookupsLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!subCategoryRequestKey) return;
    let alive = true;
    const categoryIds = subCategoryRequestKey.split('|');
    void Promise.all(categoryIds.map((id) => getSubFileCategories(id))).then((results) => {
      if (!alive) return;
      const merged = new Map<string, CaseTrackingLookupOption>();
      for (const res of results) {
        if (!res.success) continue;
        for (const item of res.data) merged.set(item.id, { id: item.id, name: item.name });
      }
      setSubFileCategoryOptions([...merged.values()].sort((a, b) => a.name.localeCompare(b.name)));
      setResolvedSubCategoryKey(subCategoryRequestKey);
    });
    return () => {
      alive = false;
    };
  }, [subCategoryRequestKey]);

  useEffect(() => {
    if (!lookupsLoaded) return;

    const requestKey = workflowStageRequestKey;
    if (requestKey === '' || requestKey === '|pending|') return;

    let alive = true;
    const controller = new AbortController();
    const parsed = JSON.parse(requestKey) as { categoryIds: string[]; subCategoryIds: string[] };

    void listWorkflowPerformanceWorkflowStagesCascade({
      categoryIds: parsed.categoryIds,
      subCategoryIds: parsed.subCategoryIds,
      signal: controller.signal,
    }).then((res) => {
      if (!alive || controller.signal.aborted) return;
      if (res.success) {
        setWorkflowStageOptionsAll(res.data.map((o) => ({ id: o.id, name: o.name })));
      } else {
        setWorkflowStageOptionsAll([]);
      }
      setResolvedWorkflowStageKey(requestKey);
    });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [workflowStageRequestKey, lookupsLoaded]);

  const userNames = userOptions.map((o) => o.name);

  return {
    agencyNames: namesFromOptions(agencyOptions, REPORT_SUBMITTED_AGENCIES),
    taskStatusNames: namesFromOptions(taskStatusOptions, REPORT_TASK_STATUSES),
    workflowStageNames: workflowStageOptions.map((o) => o.name),
    allocationOfficerNames: userNames,
    assignedOfficerNames: userNames,
    supervisorNames: userNames,
    fileCategoryNames: fileCategoryOptions.map((c) => c.name),
    subFileCategoryNames: activeSubFileCategoryOptions.map((s) => s.name),
    urgencyLevelNames: urgencyLevelOptions.map((u) => u.name),
    mapAgencyNamesToIds: (names: string[]) => mapCaseTrackingLookupNamesToIds(agencyOptions, names),
    mapTaskStatusNamesToIds: (names: string[]) => mapCaseTrackingLookupNamesToIds(taskStatusOptions, names),
    mapWorkflowStageNamesToIds: (names: string[]) =>
      mapCaseTrackingLookupNamesToIds(activeWorkflowStageOptions, names, true),
    mapAllocationOfficerNamesToIds: mapUserNamesToIds,
    mapAssignedOfficerNamesToIds: mapUserNamesToIds,
    mapSupervisorNamesToIds: mapUserNamesToIds,
    mapFileCategoryNamesToIds: (names: string[]) => mapCaseTrackingLookupNamesToIds(fileCategoryOptions, names),
    mapSubFileCategoryNamesToIds: (names: string[]) =>
      mapCaseTrackingLookupNamesToIds(activeSubFileCategoryOptions, names),
    mapUrgencyLevelNamesToIds: (names: string[]) => mapCaseTrackingLookupNamesToIds(urgencyLevelOptions, names),
    subFileCategoryDisabled: selectedFileCategoryNames.length === 0,
    workflowStageDisabled: selectedFileCategoryNames.length === 0,
    lookupsLoading: !lookupsLoaded,
    subLookupsLoading,
    workflowStagesLoading,
  };
}
